import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import {
  AgentEvents,
  ListAutopilotsResponse,
  AutopilotSummary,
  ListSampleRoutinesResponse,
  SampleRoutine,
  RoutineCategory,
} from '@dreamai/types';
import { usePusher } from '../../contexts/PusherContext';
import api from '../../api';
import { ReactComponent as PlusIcon } from '../../icons/plus.svg';
import { ReactComponent as SunSettingIcon } from '../../icons/sun-setting.svg';
import { ReactComponent as StarIcon } from '../../icons/star-06.svg';
import './routines-section.scss';

interface RoutinesSectionProps {
  onCreateRoutine: () => void;
  onViewRoutine?: (autopilotId: string, routineName: string) => void;
  onViewSampleRoutine?: (sampleRoutine: SampleRoutine) => void;
}

const CATEGORY_DISPLAY_ORDER: RoutineCategory[] = [
  'admin',
  'business',
  'finances',
  'travel',
  'groceries',
  'shopping',
  'social',
  'family',
  'other',
];

function capitalize(s: string): string {
  return s.charAt(0).toUpperCase() + s.slice(1);
}

const RoutinesSection: React.FC<RoutinesSectionProps> = ({
  onCreateRoutine,
  onViewRoutine,
  onViewSampleRoutine,
}) => {
  const { getAccessTokenSilently } = useAuth0();
  const [routines, setRoutines] = useState<AutopilotSummary[]>([]);
  const [sampleRoutines, setSampleRoutines] = useState<SampleRoutine[]>([]);
  const [activeTab, setActiveTab] = useState<string>('');
  const [initialLoading, setInitialLoading] = useState(true);

  const fetchData = useCallback(async () => {
    try {
      const token = await getAccessTokenSilently();
      const [routinesRes, samplesRes] = await Promise.all([
        api.get<ListAutopilotsResponse>('/autopilots', {
          headers: { Authorization: `Bearer ${token}` },
        }),
        api.get<ListSampleRoutinesResponse>('/sample-routines', {
          headers: { Authorization: `Bearer ${token}` },
        }),
      ]);
      if (routinesRes.data.success) {
        setRoutines(routinesRes.data.autopilots);
      }
      if (samplesRes.data.success) {
        setSampleRoutines(samplesRes.data.sample_routines);
      }
    } catch (err) {
      console.error('Failed to load routines:', err);
    } finally {
      setInitialLoading(false);
    }
  }, [getAccessTokenSilently]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Re-fetch list when a routine is created or updated
  const pusherContext = usePusher(true);
  const userChannel = pusherContext?.userChannel;

  useEffect(() => {
    if (!userChannel) return;

    const refresh = () => {
      fetchData();
    };

    userChannel.bind(AgentEvents.AUTOPILOT_CREATED, refresh);
    userChannel.bind(AgentEvents.AUTOPILOT_UPDATED, refresh);
    return () => {
      userChannel.unbind(AgentEvents.AUTOPILOT_CREATED, refresh);
      userChannel.unbind(AgentEvents.AUTOPILOT_UPDATED, refresh);
    };
  }, [userChannel, fetchData]);

  // Build tabs from categories that have at least one routine or sample routine.
  // Routines with empty categories fall into "other" so they're always visible.
  const tabs = useMemo(() => {
    const populated = new Set<string>();

    for (const r of routines) {
      if (r.categories && r.categories.length > 0) {
        r.categories.forEach((c) => populated.add(c.toLowerCase()));
      } else {
        populated.add('other');
      }
    }
    for (const s of sampleRoutines) {
      if (s.category) populated.add(s.category.toLowerCase());
    }

    return CATEGORY_DISPLAY_ORDER.filter((c) => populated.has(c));
  }, [routines, sampleRoutines]);

  // Auto-select first tab when tabs change and current tab is invalid
  useEffect(() => {
    if (
      tabs.length > 0 &&
      (!activeTab || !tabs.includes(activeTab as RoutineCategory))
    ) {
      setActiveTab(tabs[0]);
    }
  }, [tabs, activeTab]);

  const totalActiveRoutines = routines.length;

  // Routines matching the active tab's category.
  // Routines with empty categories are shown under "other".
  const filteredRoutines = useMemo(
    () =>
      routines.filter((r) => {
        if (activeTab === 'other') {
          return (
            !r.categories ||
            r.categories.length === 0 ||
            r.categories.includes('other' as RoutineCategory)
          );
        }
        return r.categories?.includes(activeTab as RoutineCategory);
      }),
    [routines, activeTab],
  );

  // Sample routines matching the active tab's category
  const filteredSamples = useMemo(
    () => sampleRoutines.filter((s) => s.category?.toLowerCase() === activeTab),
    [sampleRoutines, activeTab],
  );

  // IDs of routines already linked to a sample (to avoid showing duplicates)
  const linkedRoutineIds = useMemo(
    () =>
      new Set(
        filteredSamples
          .map((s) => s.linked_autopilot_id)
          .filter(Boolean) as string[],
      ),
    [filteredSamples],
  );

  // User routines in this tab that aren't already shown via a sample link
  const standaloneRoutines = useMemo(
    () => filteredRoutines.filter((r) => !linkedRoutineIds.has(r.id)),
    [filteredRoutines, linkedRoutineIds],
  );

  if (initialLoading) return null;

  return (
    <section className='routines-section'>
      <header className='routines-section-header'>
        <div className='routines-section-header-left'>
          <span className='routines-section-title'>Routines</span>
          {totalActiveRoutines > 0 && (
            <span className='routines-section-count'>
              {totalActiveRoutines}
            </span>
          )}
        </div>
        <div className='routines-section-header-right'>
          <button
            type='button'
            className='routines-section-pill-btn'
            onClick={onCreateRoutine}
          >
            <PlusIcon className='routines-section-pill-btn-icon' />
            New
          </button>
        </div>
      </header>

      {tabs.length > 0 && (
        <div className='routines-section-tabs'>
          {tabs.map((tab) => (
            <button
              key={tab}
              type='button'
              className={`routines-section-tab ${activeTab === tab ? 'active' : ''}`}
              onClick={() => setActiveTab(tab)}
            >
              {capitalize(tab)}
            </button>
          ))}
        </div>
      )}

      <div className='routines-section-list'>
        {tabs.length === 0 ? (
          <div className='routines-section-empty'>
            <p className='routines-section-empty-text'>
              You haven&apos;t created any routines yet.
            </p>
            <button
              type='button'
              className='routines-section-empty-btn'
              onClick={onCreateRoutine}
            >
              Create a routine
            </button>
          </div>
        ) : (
          <>
            {standaloneRoutines.map((routine) => (
              <button
                key={routine.id}
                type='button'
                className='routines-section-row'
                onClick={() => onViewRoutine?.(routine.id, routine.name)}
              >
                <div
                  className={`routines-section-row-icon ${routine.active ? 'active' : ''}`}
                >
                  <CategoryIcon category={activeTab} />
                </div>
                <span className='routines-section-row-name'>
                  {routine.name}
                </span>
                {routine.contributes_to_daily_briefing && routine.active && (
                  <span
                    className='routines-section-row-briefing-icon'
                    data-tooltip='Included in your Daily Briefing'
                  >
                    <SunSettingIcon />
                  </span>
                )}
                <span
                  className={`routines-section-row-badge ${routine.active ? 'active' : 'inactive'}`}
                >
                  {routine.active ? 'Active' : 'Inactive'}
                </span>
              </button>
            ))}

            {filteredSamples.map((sample) => {
              const isSetUp = !!sample.linked_autopilot_id;
              return (
                <button
                  key={sample.id}
                  type='button'
                  className='routines-section-row'
                  onClick={() => {
                    if (isSetUp && sample.linked_autopilot_id) {
                      onViewRoutine?.(sample.linked_autopilot_id, sample.name);
                    } else {
                      onViewSampleRoutine?.(sample);
                    }
                  }}
                >
                  <div
                    className={`routines-section-row-icon ${isSetUp ? 'active' : ''}`}
                  >
                    <CategoryIcon category={activeTab} />
                  </div>
                  <span className='routines-section-row-name'>
                    {sample.name}
                  </span>
                  {isSetUp ? (
                    <span className='routines-section-row-badge active'>
                      Active
                    </span>
                  ) : (
                    <>
                      <span className='routines-section-row-tag'>
                        <StarIcon />
                        Recommended
                      </span>
                      <span className='routines-section-row-badge start'>
                        Start
                      </span>
                    </>
                  )}
                </button>
              );
            })}

            {standaloneRoutines.length === 0 &&
              filteredSamples.length === 0 && (
                <div className='routines-section-empty'>
                  <p className='routines-section-empty-text'>
                    No routines in this category yet.
                  </p>
                </div>
              )}
          </>
        )}
      </div>
    </section>
  );
};

export const CategoryIcon: React.FC<{ category: string }> = ({ category }) => {
  const props = {
    width: 24,
    height: 24,
    viewBox: '0 0 24 24',
    fill: 'none' as const,
  };
  const stroke = {
    stroke: 'currentColor',
    strokeWidth: 1.5,
    strokeLinecap: 'round' as const,
    strokeLinejoin: 'round' as const,
  };

  switch (category) {
    case 'admin':
      return (
        <svg {...props}>
          <path
            d='M6 10V4.5C6 3.67157 6.67157 3 7.5 3C8.32843 3 9 3.67157 9 4.5V10C9 11.6569 7.65685 13 6 13C4.34315 13 3 11.6569 3 10V6M12.5 2H15.2C16.8802 2 17.7202 2 18.362 2.32698C18.9265 2.6146 19.3854 3.07354 19.673 3.63803C20 4.27976 20 5.11984 20 6.8V17.2C20 18.8802 20 19.7202 19.673 20.362C19.3854 20.9265 18.9265 21.3854 18.362 21.673C17.7202 22 16.8802 22 15.2 22H8.8C7.11984 22 6.27976 22 5.63803 21.673C5.07354 21.3854 4.6146 20.9265 4.32698 20.362C4 19.7202 4 18.8802 4 17.2V16.5'
            {...stroke}
          />
        </svg>
      );
    case 'travel':
      return (
        <svg {...props}>
          <path
            d='M8 21V7C8 6.07003 8 5.60504 8.10222 5.22354C8.37962 4.18827 9.18827 3.37962 10.2235 3.10222C10.605 3 11.07 3 12 3C12.93 3 13.395 3 13.7765 3.10222C14.8117 3.37962 15.6204 4.18827 15.8978 5.22354C16 5.60504 16 6.07003 16 7V13.5M16 17.5V21M6.8 21H17.2C18.8802 21 19.7202 21 20.362 20.673C20.9265 20.3854 21.3854 19.9265 21.673 19.362C22 18.7202 22 17.8802 22 16.2V11.8C22 10.1198 22 9.27976 21.673 8.63803C21.3854 8.07354 20.9265 7.6146 20.362 7.32698C19.7202 7 18.8802 7 17.2 7H6.8C5.11984 7 4.27976 7 3.63803 7.32698C3.07354 7.6146 2.6146 8.07354 2.32698 8.63803C2 9.27976 2 10.1198 2 11.8V16.2C2 17.8802 2 18.7202 2.32698 19.362C2.6146 19.9265 3.07354 20.3854 3.63803 20.673C4.27976 21 5.11984 21 6.8 21ZM12.8 17.5H17.7C17.98 17.5 18.12 17.5 18.227 17.4455C18.3211 17.3976 18.3976 17.3211 18.4455 17.227C18.5 17.12 18.5 16.98 18.5 16.7V14.3C18.5 14.02 18.5 13.88 18.4455 13.773C18.3976 13.6789 18.3211 13.6024 18.227 13.5545C18.12 13.5 17.98 13.5 17.7 13.5H12.8C12.52 13.5 12.38 13.5 12.273 13.5545C12.1789 13.6024 12.1024 13.6789 12.0545 13.773C12 13.88 12 14.02 12 14.3V16.7C12 16.98 12 17.12 12.0545 17.227C12.1024 17.3211 12.1789 17.3976 12.273 17.4455C12.38 17.5 12.52 17.5 12.8 17.5Z'
            {...stroke}
          />
        </svg>
      );
    case 'groceries':
      return (
        <svg {...props}>
          <path
            d='M6 2L3 6V20C3 20.5304 3.21071 21.0391 3.58579 21.4142C3.96086 21.7893 4.46957 22 5 22H19C19.5304 22 20.0391 21.7893 20.4142 21.4142C20.7893 21.0391 21 20.5304 21 20V6L18 2H6ZM3 6H21M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10'
            {...stroke}
          />
        </svg>
      );
    case 'finances':
      return (
        <svg {...props}>
          <path
            d='M12 2V22M17 5H9.5C8.57174 5 7.6815 5.36875 7.02513 6.02513C6.36875 6.6815 6 7.57174 6 8.5C6 9.42826 6.36875 10.3185 7.02513 10.9749C7.6815 11.6313 8.57174 12 9.5 12H14.5C15.4283 12 16.3185 12.3687 16.9749 13.0251C17.6313 13.6815 18 14.5717 18 15.5C18 16.4283 17.6313 17.3185 16.9749 17.9749C16.3185 18.6313 15.4283 19 14.5 19H6'
            {...stroke}
          />
        </svg>
      );
    case 'social':
      return (
        <svg {...props}>
          <path
            d='M15.5455 9.92543C15.9195 9.26103 16.2313 8.66151 16.4236 8.20521C17.3573 5.98947 16.434 3.44077 14.1769 2.40112C11.9199 1.36148 9.65341 2.4395 8.65871 4.52093C6.75657 3.2157 4.21918 3.40739 2.81989 5.44424C1.42059 7.48108 1.85975 10.142 3.77629 11.594C4.6461 12.253 6.36636 13.2242 7.98596 14.0884M16.2972 11.7499C15.8751 9.482 13.9454 7.82334 11.5156 8.27415C9.08592 8.72497 7.51488 10.9171 7.84335 13.299C8.10725 15.2127 9.56392 19.7027 10.1264 21.394C10.2032 21.6248 10.2415 21.7402 10.3175 21.8206C10.3837 21.8907 10.4717 21.9416 10.5655 21.9638C10.6732 21.9894 10.7923 21.9649 11.0306 21.916C12.7765 21.5575 17.3933 20.574 19.1826 19.8457C21.4096 18.9392 22.5589 16.4841 21.6981 14.153C20.8372 11.8219 18.4723 10.9815 16.2972 11.7499Z'
            {...stroke}
          />
        </svg>
      );
    case 'shopping':
      return (
        <svg {...props}>
          <path
            d='M20.59 13.41L13.42 20.58C13.2343 20.766 13.0137 20.9135 12.7709 21.0141C12.5281 21.1148 12.2678 21.1666 12.005 21.1666C11.7422 21.1666 11.4819 21.1148 11.2391 21.0141C10.9963 20.9135 10.7757 20.766 10.59 20.58L2 12V2H12L20.59 10.59C20.9625 10.9647 21.1716 11.4716 21.1716 12C21.1716 12.5284 20.9625 13.0353 20.59 13.41Z'
            {...stroke}
          />
          <path d='M7 7H7.01' {...stroke} strokeWidth={2} />
        </svg>
      );
    case 'family':
      return (
        <svg {...props}>
          <path
            d='M16 3.46776C17.4817 4.20411 18.5 5.73314 18.5 7.5C18.5 9.26686 17.4817 10.7959 16 11.5322M18 16.7664C19.5115 17.4503 20.8725 18.565 22 20M2 20C3.94649 17.5226 6.58918 16 9.5 16C12.4108 16 15.0535 17.5226 17 20M14 7.5C14 9.98528 11.9853 12 9.5 12C7.01472 12 5 9.98528 5 7.5C5 5.01472 7.01472 3 9.5 3C11.9853 3 14 5.01472 14 7.5Z'
            {...stroke}
          />
        </svg>
      );
    case 'business':
      return (
        <svg {...props}>
          <path
            d='M18.48 2.64C18.304 2.40533 18.216 2.288 18.1045 2.20338C18.0057 2.12842 17.8938 2.0725 17.7746 2.03845C17.64 2 17.4933 2 17.2 2H6.8C6.50667 2 6.36 2 6.22539 2.03845C6.10616 2.0725 5.9943 2.12842 5.89552 2.20338C5.784 2.288 5.696 2.40533 5.52 2.64L3.96 4.72C3.65102 5.13198 3.49652 5.33797 3.50011 5.51039C3.50323 5.66044 3.57358 5.80115 3.69175 5.89368C3.82754 6 4.08503 6 4.6 6H19.4C19.915 6 20.1725 6 20.3083 5.89368C20.4264 5.80115 20.4968 5.66044 20.4999 5.51039C20.5035 5.33797 20.349 5.13198 20.04 4.72L18.48 2.64ZM5.52 2.64L3.64 5.14666C3.40254 5.46328 3.28381 5.62159 3.1995 5.79592C3.12469 5.95062 3.07012 6.11431 3.03715 6.28296C3 6.47301 3 6.6709 3 7.06666L3 18.8C3 19.9201 3 20.4802 3.21799 20.908C3.40973 21.2843 3.71569 21.5903 4.09202 21.782C4.51984 22 5.07989 22 6.2 22L17.8 22C18.9201 22 19.4802 22 19.908 21.782C20.2843 21.5903 20.5903 21.2843 20.782 20.908C21 20.4802 21 19.9201 21 18.8V7.06667C21 6.6709 21 6.47301 20.9628 6.28296C20.9299 6.11431 20.8753 5.95062 20.8005 5.79592C20.7162 5.62159 20.5975 5.46328 20.36 5.14667L18.48 2.64M16 10C16 11.0609 15.5786 12.0783 14.8284 12.8284C14.0783 13.5786 13.0609 14 12 14C10.9391 14 9.92172 13.5786 9.17157 12.8284C8.42143 12.0783 8 11.0609 8 10'
            {...stroke}
          />
        </svg>
      );
    default:
      return (
        <svg {...props}>
          <path
            d='M6 10V4.5C6 3.67157 6.67157 3 7.5 3C8.32843 3 9 3.67157 9 4.5V10C9 11.6569 7.65685 13 6 13C4.34315 13 3 11.6569 3 10V6M12.5 2H15.2C16.8802 2 17.7202 2 18.362 2.32698C18.9265 2.6146 19.3854 3.07354 19.673 3.63803C20 4.27976 20 5.11984 20 6.8V17.2C20 18.8802 20 19.7202 19.673 20.362C19.3854 20.9265 18.9265 21.3854 18.362 21.673C17.7202 22 16.8802 22 15.2 22H8.8C7.11984 22 6.27976 22 5.63803 21.673C5.07354 21.3854 4.6146 20.9265 4.32698 20.362C4 19.7202 4 18.8802 4 17.2V16.5'
            {...stroke}
          />
        </svg>
      );
  }
};

export default RoutinesSection;
